import { useState } from 'react';

const AddEditAgentModal = ({ show, onHide, onAgentAdded, loading = false }) => {
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleClose = () => {
    setEmail('');
    setError('');
    setIsSubmitting(false);
    onHide();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!email.trim()) {
      setError('Email address is required');
      return;
    }

    if (!emailRegex.test(email)) {
      setError('Please enter a valid email address');
      return;
    }

    setError('');
    setIsSubmitting(true);

    try {
      await onAgentAdded(email);
      handleClose();
    } catch (err) {
      setError('This email isn\'t registered with elfoA2P. Only existing users can be invited as agents.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      handleClose();
    }
  };

  if (!show) {
    return null;
  }

  return (
    <div className="agent-modal__overlay" onClick={handleOverlayClick}>
      <div className="agent-modal__container">
        {/* Close Button */}
        <button
          className="agent-modal__close-button"
          onClick={handleClose}
          disabled={isSubmitting}
        >
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
            <path
              d="M15 5L5 15M5 5L15 15"
              stroke="#737C8A"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>

        {/* Content */}
        <div className="agent-modal__content">
          {/* Header with Icon */}
          <div className="agent-modal__header">
            <div className="agent-modal__icon">
              <svg width="24" height="24" viewBox="0 0 38 39" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect x="4" y="4.5" width="30" height="30" rx="15" fill="#C4E3D1" />
                <rect x="4" y="4.5" width="30" height="30" rx="15" stroke="#EFF0EF" strokeOpacity="0.4" strokeWidth="8" />
                <path d="M19 19.5C18.3125 19.5 17.724 19.2552 17.2344 18.7656C16.7448 18.276 16.5 17.6875 16.5 17C16.5 16.3125 16.7448 15.724 17.2344 15.2344C17.724 14.7448 18.3125 14.5 19 14.5C19.6875 14.5 20.276 14.7448 20.7656 15.2344C21.2552 15.724 21.5 16.3125 21.5 17C21.5 17.6875 21.2552 18.276 20.7656 18.7656C20.276 19.2552 19.6875 19.5 19 19.5ZM14 24.5V22.75C14 22.3958 14.0911 22.0703 14.2734 21.7734C14.4557 21.4766 14.6979 21.25 15 21.0938C15.6458 20.7708 16.3021 20.5286 16.9688 20.3672C17.6354 20.2057 18.3125 20.125 19 20.125C19.6875 20.125 20.3646 20.2057 21.0312 20.3672C21.6979 20.5286 22.3542 20.7708 23 21.0938C23.3021 21.25 23.5443 21.4766 23.7266 21.7734C23.9089 22.0703 24 22.3958 24 22.75V24.5H14Z" fill="#27AE60" />
              </svg>
            </div>
            <div className="agent-modal__title-section">
              <h2 className="agent-modal__title">Add New Agents</h2>
              <p className="agent-modal__subtitle">
                Upon adding a new agent, you will gain the ability to assign conversation their account.
              </p>
            </div>
          </div>

          {/* Form */}
          <div className="agent-modal__form">
            <div className="agent-modal__field">
              <label className="agent-modal__label">Email</label>
              <input
                type="email"
                className="agent-modal__input"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Email Address"
                disabled={isSubmitting}
              />
              {error && (
                <div className="agent-modal__error">{error}</div>
              )}
            </div>
          </div>

          {/* Actions */}
          <div className="agent-modal__actions">
            <button
              className="agent-modal__button agent-modal__button--primary"
              onClick={handleSubmit}
              disabled={isSubmitting || !email.trim()}
            >
              {isSubmitting ? (
                <div className="agent-modal__loading">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                    <circle
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeDasharray="31.416"
                      strokeDashoffset="31.416"
                      style={{
                        animation: 'spin 1s linear infinite',
                        transformOrigin: 'center'
                      }}
                    />
                  </svg>
                  Sending...
                </div>
              ) : (
                'Add'
              )}
            </button>
          </div>
        </div>
      </div>

      <style jsx>{`
        @keyframes spin {
          to {
            stroke-dashoffset: 0;
          }
        }

        .agent-modal__overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background-color: rgba(0, 0, 0, 0.5);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1050;
          padding: 20px;
        }

        .agent-modal__container {
          background: white;
          border-radius: 16px;
          box-shadow: 0px 2.75px 9px 0px rgba(0, 0, 0, 0.19), 0px 0.25px 3px 0px rgba(0, 0, 0, 0.04);
          width: 100%;
          max-width: 500px;
          max-height: 90vh;
          overflow-y: auto;
          position: relative;
        }

        .agent-modal__close-button {
          position: absolute;
          top: 16px;
          right: 16px;
          background: none;
          border: none;
          cursor: pointer;
          padding: 8px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: background-color 0.2s;
          z-index: 1;
        }

        .agent-modal__close-button:hover {
          background-color: #f5f5f5;
        }

        .agent-modal__close-button:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .agent-modal__content {
          padding: 32px;
        }

        .agent-modal__header {
          display: flex;
          flex-direction: column;
          align-items: flex-start;
          gap: 16px;
          margin-bottom: 32px;
        }

        .agent-modal__icon {
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .agent-modal__title-section {
          width: 100%;
        }

        .agent-modal__title {
          font-family: 'Poppins', sans-serif;
          font-weight: 600;
          font-size: 24px;
          line-height: 1.3;
          color: #344054;
          margin: 0 0 8px 0;
        }

        .agent-modal__subtitle {
          font-family: 'Poppins', sans-serif;
          font-weight: 400;
          font-size: 16px;
          line-height: 1.5;
          color: #667085;
          margin: 0;
        }

        .agent-modal__form {
          margin-bottom: 32px;
        }

        .agent-modal__field {
          margin-bottom: 20px;
        }

        .agent-modal__label {
          display: block;
          font-family: 'Poppins', sans-serif;
          font-weight: 500;
          font-size: 14px;
          line-height: 1.4;
          color: #344054;
          margin-bottom: 8px;
        }

        .agent-modal__input {
          width: 100%;
          padding: 12px 16px;
          border: 1px solid #D0D5DD;
          border-radius: 8px;
          font-family: 'Poppins', sans-serif;
          font-size: 16px;
          line-height: 1.5;
          color: #344054;
          background-color: white;
          transition: border-color 0.2s, box-shadow 0.2s;
        }

        .agent-modal__input:focus {
          outline: none;
          border-color: #0BA9E0;
          box-shadow: 0 0 0 3px rgba(11, 169, 224, 0.1);
        }

        .agent-modal__input:disabled {
          background-color: #f9fafb;
          color: #667085;
          cursor: not-allowed;
        }

        .agent-modal__input::placeholder {
          color: #98A2B3;
        }

        .agent-modal__error {
          color: #dc3545;
          font-family: 'Poppins', sans-serif;
          font-size: 14px;
          margin-top: 8px;
        }

        .agent-modal__actions {
          display: flex;
          justify-content: flex-end;
          gap: 12px;
        }

        .agent-modal__button {
          padding: 12px 24px;
          border-radius: 8px;
          font-family: 'Poppins', sans-serif;
          font-weight: 500;
          font-size: 16px;
          line-height: 1.5;
          cursor: pointer;
          transition: all 0.2s;
          border: none;
          display: flex;
          align-items: center;
          gap: 8px;
          justify-content: center;
          flex: 0 0 50%;
        }

        .agent-modal__button--primary {
          background-color: #0BA9E0;
          color: white;
        }

        .agent-modal__button--primary:hover:not(:disabled) {
          background-color: #0891c7;
        }

        .agent-modal__button:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .agent-modal__loading {
          display: flex;
          align-items: center;
          gap: 8px;
        }

        @media (max-width: 640px) {
          .agent-modal__container {
            margin: 20px;
            max-width: calc(100vw - 40px);
          }

          .agent-modal__content {
            padding: 24px;
          }

          .agent-modal__title {
            font-size: 20px;
          }

          .agent-modal__subtitle {
            font-size: 14px;
          }
        }
      `}</style>
    </div>
  );
};

export default AddEditAgentModal;
